import { memo, useState } from 'react';
import { AcoesTriagem, BotaoCancelar, BotaoManter } from '@/components/kanban/triagem/AcoesTriagem';
import { formatarDocumento } from '@/components/kanban/triagem/segmentoTriagem';
import { ManterComNotaModal } from '@/components/kanban/ManterComNotaModal';
import { diasParadoNaFila } from '@/components/kanban/triagem/diasParadoNaFila';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Check, Loader2, DollarSign, CalendarClock, Eye, ShieldX } from 'lucide-react';
import { AgendaInstalacaoPopover } from '@/components/kanban/instalacao/AgendaInstalacaoPopover';
import { CopyOnClick } from '@/components/planilha/CopyOnClick';
import { CotacaoInputPopover } from './CotacaoInputPopover';
import { usePainelPedido } from '@/contexts/PainelPedidoContext';
import { CancelarItemModal } from '@/components/pedido/CancelarItemModal';
import { format, parseISO } from 'date-fns';
import type { PedidoTriagemSolar } from './useTriagemSolar';
import { resumoProdutos } from '@/components/kanban/triagem/triagemPedidoUtils';
import { PrazoSlaBadge } from '@/components/kanban/PrazoSlaBadge';
import { TRIAGEM_BTN, TRIAGEM_BTN_PRIMARIO } from '@/components/kanban/triagem/triagemTabela';

interface TriagemSolarRowProps {
  pedido: PedidoTriagemSolar;
  familia?: 'MOVEL' | 'FIXA';
  onMoverParaErro: (itemId: string) => void;
  onMoverParaFinal: (itemId: string) => void;
  onConfirmarCotacao: (itemId: string, numeroCotacao: string) => void;
  onMoverParaCreditoNegado: (itemId: string) => void;
  onMoverParaCreditoReprovado: (itemId: string) => void;
  onEntrarEmInstalacao: (itemId: string, dataIso: string | null) => void;
  podeEmInstalacao: boolean;
  /** Só este pedido está esperando o banco; os outros da fila seguem livres. */
  ocupada: boolean;
  isMovingToErro: boolean;
  isMovingToFinal: boolean;
  isConfirmingCotacao: boolean;
  isMovingToCreditoNegado: boolean;
  isMovingToCreditoReprovado: boolean;
  isEntrandoInstalacao: boolean;
}


function formatDataInstalacao(data: string | null): string {
  if (!data) return '-';
  try {
    return format(parseISO(data), 'dd/MM/yyyy');
  } catch {
    return '-';
  }
}

export const TriagemSolarRow = memo(function TriagemSolarRow({
  pedido,
  familia,
  onMoverParaErro,
  onMoverParaFinal,
  onConfirmarCotacao,
  onMoverParaCreditoNegado,
  onMoverParaCreditoReprovado,
  onEntrarEmInstalacao,
  podeEmInstalacao,
  ocupada,
  isMovingToErro,
  isMovingToFinal,
  isConfirmingCotacao,
  isMovingToCreditoNegado,
  isMovingToCreditoReprovado,
  isEntrandoInstalacao,
}: TriagemSolarRowProps) {
  const [manterModalOpen, setManterModalOpen] = useState(false);
  const [cancelarModalOpen, setCancelarModalOpen] = useState(false);
  const [agendaAberta, setAgendaAberta] = useState(false);
  /* A linha é o pedido (Douglas 24/10/2026): os botões mandam a 1ª linha e o
     hook leva todas as linhas do pedido nesta coluna. */
  const item = pedido.itens[0];
  const linhas = pedido.itens.length;
  const { resumo, completo } = resumoProdutos(pedido.itens);
  const soTtFixa = pedido.itens.every((i) => i.produto === 'TT FIXA');
  const cnpjFormatado = formatarDocumento(pedido.documento || pedido.cnpj);
  const isFixa = familia === 'FIXA';
  const isProcessing = ocupada;
  const semPermissaoInstalacao = 'Em instalação — só a BKO da tramitação ou a supervisora do backoffice (no CPF, também a supervisora da equipe CPF)';

  const { open: openPedido } = usePainelPedido();

  const handleOpenPedido = (e: React.MouseEvent) => {
    e.stopPropagation();
    openPedido(item.pedido_id, 'visao360');
  };

  return (
    <tr className="box-border h-[33px] border-b border-border/30 transition-colors hover:bg-muted/30">
      <td className="px-2 py-0 align-middle">
        <CopyOnClick value={pedido.documento || pedido.cnpj} className="tabular-nums text-xs">
          {cnpjFormatado}
        </CopyOnClick>
      </td>

      <td className="px-2 py-0 align-middle">
        <span className="block truncate text-xs font-semibold text-foreground" title={pedido.razao_social}>
          {pedido.razao_social || '-'}
        </span>
      </td>

      {!isFixa && (
        <td className="px-2 py-0 align-middle">
          {pedido.cotacoes.length ? (
            <CopyOnClick value={pedido.cotacoes.join(' ')} className="block truncate text-xs tabular-nums">
              {pedido.cotacoes.join(' · ')}
            </CopyOnClick>
          ) : (
            <span className="text-[11px] text-primary dark:text-[#C87484]" title="O número da cotação entra no Cotação OK e vale para todas as linhas do pedido">aguardando</span>
          )}
        </td>
      )}

      <td className="px-2 py-0 align-middle">
        <button
          onClick={handleOpenPedido}
          className="cursor-pointer text-xs font-bold tabular-nums text-primary hover:underline dark:text-[#C87484]"
          title="Abrir detalhes do pedido"
        >
          {pedido.numero_pedido}
        </button>
      </td>

      <td className="px-2 py-0 align-middle">
        <span className="block truncate text-xs text-muted-foreground" title={completo}>
          {resumo || '-'}
        </span>
      </td>

      {!isFixa && (
        <td className="px-2 py-0 align-middle">
          <span className="text-xs text-muted-foreground" title={linhas > 1 ? `${linhas} linhas no pedido` : undefined}>{pedido.quantidade || '-'}</span>
        </td>
      )}

      {!isFixa && (
        <td className="px-2 py-0 align-middle">
          <span className="text-xs text-muted-foreground">
            {pedido.valor ? `R$ ${pedido.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : '-'}
          </span>
        </td>
      )}

      {isFixa && (
        <td className="px-2 py-0 align-middle">
          <span className="text-xs text-muted-foreground">{formatDataInstalacao(pedido.data_agendamento)}</span>
        </td>
      )}

      <td className="px-2 py-0 align-middle">
        <PrazoSlaBadge
          pedidoId={pedido.pedido_id}
          coluna={isFixa ? 'INPUT REALIZADO FIXA' : 'INPUT REALIZADO MOVEL'}
          entradaColunaEm={pedido.entrada_coluna_em}
          prazoHoras={isFixa ? 'agendamento' : 5}
          dataAgendamento={isFixa ? pedido.data_agendamento : undefined}
        />
      </td>

      {/* Ações */}
      <td className="px-2 py-0 align-middle">
        <AcoesTriagem
          manter={
            <BotaoManter
              onClick={() => setManterModalOpen(true)}
              disabled={isProcessing}
              carregando={isMovingToFinal}
            />
          }
          avancar={
            !isFixa ? (
              <CotacaoInputPopover
                onConfirm={(numeroCotacao) => onConfirmarCotacao(item.item_id, numeroCotacao)}
                isLoading={isConfirmingCotacao}
              >
                <Button
                  size="icon"
                  variant="ghost"
                  className={TRIAGEM_BTN_PRIMARIO}
                  title={`Cotação OK — informar o número da cotação e seguir o pedido${linhas > 1 ? ` (vale para as ${linhas} linhas)` : ''}`}
                  disabled={isProcessing}
                >
                  {isConfirmingCotacao ? <Loader2 className="animate-spin" /> : <Check />}
                </Button>
              </CotacaoInputPopover>
            ) : soTtFixa ? (
              // TT Fixa não tem agenda de instalação: entra direto (29/09/2026)
              <Button
                size="icon"
                variant="ghost"
                className={TRIAGEM_BTN_PRIMARIO}
                title={podeEmInstalacao ? 'Em instalação — TT Fixa não tem agenda, o prazo é de 48 h úteis' : semPermissaoInstalacao}
                onClick={() => onEntrarEmInstalacao(item.item_id, null)}
                disabled={isProcessing || !podeEmInstalacao}
              >
                {isEntrandoInstalacao ? <Loader2 className="animate-spin" /> : <CalendarClock />}
              </Button>
            ) : (
              <AgendaInstalacaoPopover
                titulo="Mandar para Em instalação"
                descricao={pedido.data_agendamento ? 'Confira a data e o turno da agenda da Claro.' : 'Sem data não entra na fila. Marque a data e o turno da instalação.'}
                dataAtual={pedido.data_agendamento}
                confirmar="Mandar para Em instalação"
                salvando={isEntrandoInstalacao}
                aberto={agendaAberta}
                onAberto={setAgendaAberta}
                onSalvar={({ dataIso }) => { onEntrarEmInstalacao(item.item_id, dataIso); setAgendaAberta(false); }}
              >
                <Button
                  size="icon"
                  variant="ghost"
                  className={TRIAGEM_BTN_PRIMARIO}
                  title={podeEmInstalacao ? 'Em instalação — marcar data e turno e mandar o pedido para a fila de instalação' : semPermissaoInstalacao}
                  disabled={isProcessing || !podeEmInstalacao}
                >
                  {isEntrandoInstalacao ? <Loader2 className="animate-spin" /> : <CalendarClock />}
                </Button>
              </AgendaInstalacaoPopover>
            )
          }
          desvios={
            <>
                <Button
                  size="icon"
                  variant="ghost"
                  className={TRIAGEM_BTN}
                  title={isFixa ? 'Só para a móvel' : "Erro — mandar o pedido para o input manual (Erro Solar)"}
                  onClick={() => onMoverParaErro(item.item_id)}
                  disabled={isProcessing || isFixa}
                >
                  {isMovingToErro ? <Loader2 className="animate-spin" /> : <AlertTriangle />}
                </Button>
                {/* TT recursos inválidos (Douglas 05/08/2026): sem pedir o valor da
                    dívida — ele só aparece depois, quando o GC devolve a fatura do CPF. */}
                <Button
                  size="icon"
                  variant="ghost"
                  className={TRIAGEM_BTN}
                  title={isFixa ? 'Só para a móvel' : "(TT) Recursos inválidos — dívida interna: mandar para a fila de TT recursos inválidos"}
                  onClick={() => onMoverParaCreditoNegado(item.item_id)}
                  disabled={isProcessing || isFixa}
                >
                  {isMovingToCreditoNegado ? <Loader2 className="animate-spin" /> : <DollarSign />}
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  className={TRIAGEM_BTN}
                  title={isFixa ? 'Só para a móvel' : "Crédito reprovado no Solar — mandar para CREDITO REPROVADO (quadro Crédito)"}
                  onClick={() => onMoverParaCreditoReprovado(item.item_id)}
                  disabled={isProcessing || isFixa}
                >
                  {isMovingToCreditoReprovado ? <Loader2 className="animate-spin" /> : <ShieldX />}
                </Button>
            </>
          }
          ferramentas={
            <Button
              size="icon"
              variant="ghost"
              className={TRIAGEM_BTN}
              title="Analisar — Abrir Painel do Pedido (Pedido 360)"
              onClick={() => openPedido(item.pedido_id, 'visao360')}
            >
              <Eye className="h-3 w-3" />
            </Button>
          }
          cancelar={
            <BotaoCancelar
              title={isFixa ? 'Pedido cancelado — abrir o cancelamento do item' : 'Só para a fixa — na móvel o cancelamento segue pelo Erro ou pelo crédito'}
              onClick={() => setCancelarModalOpen(true)}
              disabled={isProcessing || !isFixa}
            />
          }
        />
        <ManterComNotaModal
          open={manterModalOpen}
          onOpenChange={setManterModalOpen}
          pedidoId={item.pedido_id}
          clienteId=""
          onConfirm={() => onMoverParaFinal(item.item_id)}
          justificarFila={{
            itemId: item.item_id,
            coluna: item.coluna_atual,
            numeroPedido: item.numero_pedido,
            razaoSocial: item.razao_social,
            justificativaAtual: null,
            diasParado: diasParadoNaFila(pedido.entrada_coluna_em),
          }}
        />
        {isFixa && (
          <CancelarItemModal
            open={cancelarModalOpen}
            onOpenChange={setCancelarModalOpen}
            itemId={item.item_id}
            pedidoId={item.pedido_id}
            itemNome={resumo || pedido.numero_pedido}
          />
        )}
      </td>
    </tr>
  );
});
